// The App Setting `ROUTING_IMPORT`: Admin → Import, its Reset and the Worklist's "Imported from the
// workbook" exist only when it is exactly "on" (trimmed, case-folded); unset or anything else is off.
// The server reads it (`backend/src/routing/import-switch.ts`) and tells the pages in `/version`.

/** Whether a `ROUTING_IMPORT` value turns Admin → Import on. */
export const importSwitchOn = (value: string | null | undefined) => (value ?? "").trim().toLowerCase() === "on";

/** Admin's tabs, in order; Import only while the switch is on. */
export const ADMIN_TAB_IDS = ["divisions", "customers", "routing-rules", "assignments", "import"] as const;
export type AdminTabId = (typeof ADMIN_TAB_IDS)[number];

export const adminTabIds = (importOn: boolean): AdminTabId[] => ADMIN_TAB_IDS.filter((t) => importOn || t !== "import");

/** The tab a link (`?admin=`) opens: the one asked for while it is offered, else the first. */
export const adminTabOf = (asked: string | null | undefined, importOn: boolean): AdminTabId => {
  const tabs = adminTabIds(importOn);
  return tabs.find((t) => t === asked) ?? tabs[0]!;
};

/**
 * The Worklist's "Imported from the workbook" chip: only while the switch is on, and only when it has
 * lines to show (`count`) or a run happened (`runs`: the Worklist's `importRuns`) — never "(0)" on an
 * app nothing was imported into.
 */
export const importedChipShown = (importOn: boolean, count: number, runs: number | null) =>
  importOn && (count > 0 || (runs ?? 0) > 0);

/**
 * The address's query once Admin shows `tab` (a tab click, or Ask AI opening one): `admin=<tab>`, the
 * other keys kept, `rules=` (Routing rules' In use filter) dropped off Routing rules. `search` is
 * `location.search`, with or without its "?"; `admin=` keeps its place, else goes last.
 */
export const adminTabSearch = (search: string, tab: AdminTabId) => {
  const key = (part: string) => part.split("=")[0];
  const parts = search
    .replace(/^\?/, "")
    .split("&")
    .filter((p) => p && !(key(p) === "rules" && tab !== "routing-rules"));
  const at = parts.findIndex((p) => key(p) === "admin");
  if (at < 0) parts.push(`admin=${tab}`);
  else parts[at] = `admin=${tab}`;
  return `?${parts.join("&")}`;
};
